import * as XLSX from "xlsx";
import { HEADERS } from "../api/sheets";
import { Row } from "../types/sheets";

const HEADER_ALIASES: Record<string, string[]> = {
  "#": ["#", "No", "no", "番号"],
  ID: ["ID", "Id", "id"],
  担当者: ["担当者"],
  キャンパス: ["キャンパス"],
  "部局、施設名": ["部局、施設名", "部局・施設名", "部局施設名", "施設名"],
  建物: ["建物"],
  階: ["階"],
  設置場所: ["設置場所", "トイレ種別", "表示名②（旧）", "表示名2（旧）"],
  備考: ["備考"],
  アクセス: ["アクセス"],
  "表記名①": ["表記名①", "表記名1", "表記名"],
  緯度: ["緯度", "lat", "Lat", "Latitude"],
  経度: ["経度", "lng", "lon", "Long", "Longitude"],
  写真フォルダID: ["写真フォルダID", "写真フォルダ"],
  写真ファイルID: ["写真ファイルID", "写真ファイル"],
};

function normalize(s: unknown): string {
  return String(s ?? "").replace(/\s/g, "");
}

function pickKey(rawRow: Record<string, unknown>, target: string): string | null {
  const aliases = HEADER_ALIASES[target] || [target];
  for (const a of aliases) {
    if (Object.prototype.hasOwnProperty.call(rawRow, a)) return a;
  }
  for (const key of Object.keys(rawRow)) {
    if (aliases.some((a) => normalize(key) === normalize(a))) return key;
  }
  return null;
}

function matchesCanonicalHeader(sourceHeader: string): string | null {
  const n = normalize(sourceHeader);
  if (!n) return null;
  for (const h of HEADERS) {
    const aliases = HEADER_ALIASES[h] || [h];
    if (aliases.some((a) => normalize(a) === n)) return h;
  }
  return null;
}

export interface ExcelImportResult {
  rows: Row[];
  extraHeaders: string[];
  extraRows: string[][];
  hasAllRequired: boolean;
  isCanonicalOrder: boolean;
  sourceHeaders: string[];
}

export async function importExcelFileFull(file: File): Promise<ExcelImportResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const firstSheet = wb.SheetNames[0];
  const ws = wb.Sheets[firstSheet];

  const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: "" });
  const sourceHeaders = ((aoa[0] as unknown[]) || []).map((h) => String(h ?? ""));

  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: "" });

  const rows: Row[] = json.map((rawRow) =>
    HEADERS.map((h) => {
      const key = pickKey(rawRow, h);
      return key !== null ? String(rawRow[key] ?? "") : "";
    })
  );

  const matchedSourceHeaders = new Set<string>();
  const matchedCanonical = new Set<string>();
  for (const sh of sourceHeaders) {
    const canon = matchesCanonicalHeader(sh);
    if (canon && !matchedCanonical.has(canon)) {
      matchedCanonical.add(canon);
      matchedSourceHeaders.add(sh);
    }
  }

  const extraHeaders = sourceHeaders.filter(
    (sh) => sh && !matchedSourceHeaders.has(sh)
  );

  const extraRows: string[][] = json.map((rawRow) =>
    extraHeaders.map((eh) => String(rawRow[eh] ?? ""))
  );

  const hasAllRequired = HEADERS.every((h) => matchedCanonical.has(h));

  const canonicalOrderTopN = sourceHeaders
    .slice(0, HEADERS.length)
    .map((sh) => matchesCanonicalHeader(sh));
  const isCanonicalOrder =
    hasAllRequired &&
    canonicalOrderTopN.length === HEADERS.length &&
    canonicalOrderTopN.every((c, i) => c === HEADERS[i]);

  return {
    rows,
    extraHeaders,
    extraRows,
    hasAllRequired,
    isCanonicalOrder,
    sourceHeaders,
  };
}
